export const OFFSET_STEP_PX = 24;
export const LABEL_OFFSET_STEP_PX = 20;
export const MIN_SPAN_FOR_LABEL_OFFSET_PX = 80;
export const WORKFLOW_EDGE_TYPE = 'workflowNavigation';

/**
 * Build a stable React Flow edge id for a navigation action.
 * @param {string} source
 * @param {string} target
 * @param {number} actionIndex - index in the step's full actions array
 */
export function buildEdgeId(source, target, actionIndex) {
  return `${source}->${target}@${actionIndex}`;
}

/**
 * @param {string} id
 * @returns {{ source: string, target: string, actionIndex: number } | null}
 */
export function parseEdgeId(id) {
  if (!id || typeof id !== 'string') return null;

  const atIndex = id.lastIndexOf('@');
  if (atIndex === -1) {
    const arrowIndex = id.indexOf('->');
    if (arrowIndex === -1) return null;
    return {
      source: id.slice(0, arrowIndex),
      target: id.slice(arrowIndex + 2),
      actionIndex: 0,
    };
  }

  const actionIndex = Number(id.slice(atIndex + 1));
  if (!Number.isInteger(actionIndex) || actionIndex < 0) return null;

  const pair = id.slice(0, atIndex);
  const arrowIndex = pair.indexOf('->');
  if (arrowIndex === -1) return null;

  return {
    source: pair.slice(0, arrowIndex),
    target: pair.slice(arrowIndex + 2),
    actionIndex,
  };
}

function buildEdgePairKey(source, target) {
  return `${source}->${target}`;
}

/**
 * Compute smoothstep path offset for parallel edges sharing source+target.
 * @param {number} indexInGroup - 0-based index within the parallel group
 * @param {number} groupSize
 */
export function computeParallelOffset(indexInGroup, groupSize) {
  if (groupSize <= 1) return 0;
  return (indexInGroup - (groupSize - 1) / 2) * OFFSET_STEP_PX;
}

/**
 * Vertical (or perpendicular) label stagger for parallel edges in the same group.
 */
function computeParallelLabelOffset(indexInGroup, groupSize) {
  if (groupSize <= 1) return 0;
  return (indexInGroup - (groupSize - 1) / 2) * LABEL_OFFSET_STEP_PX;
}

function buildParallelEdgeData(existingData, indexInGroup, groupSize) {
  return {
    ...(existingData || {}),
    indexInGroup,
    groupSize,
    parallelOffset: computeParallelOffset(indexInGroup, groupSize),
    labelOffsetY: computeParallelLabelOffset(indexInGroup, groupSize),
  };
}

/**
 * Build one React Flow edge object for a navigation action.
 */
export function buildNavigationEdge({
  source,
  target,
  actionIndex,
  label,
  indexInGroup = 0,
  groupSize = 1,
}) {
  return {
    id: buildEdgeId(source, target, actionIndex),
    source,
    target,
    type: WORKFLOW_EDGE_TYPE,
    label: label || '',
    data: buildParallelEdgeData({ actionIndex }, indexInGroup, groupSize),
  };
}

/**
 * Recompute parallelOffset for every edge based on how many edges share source+target.
 */
export function reconcileParallelOffsets(edges) {
  const groupCounts = new Map();
  edges.forEach((edge) => {
    const key = buildEdgePairKey(edge.source, edge.target);
    groupCounts.set(key, (groupCounts.get(key) || 0) + 1);
  });

  const groupIndices = new Map();

  return edges.map((edge) => {
    const key = buildEdgePairKey(edge.source, edge.target);
    const groupSize = groupCounts.get(key) || 1;
    const indexInGroup = groupIndices.get(key) || 0;
    groupIndices.set(key, indexInGroup + 1);

    return {
      ...edge,
      type: WORKFLOW_EDGE_TYPE,
      data: buildParallelEdgeData(edge.data, indexInGroup, groupSize),
    };
  });
}

/**
 * Build React Flow edges for all navigation actions in workflow steps.
 * @param {Array<{ id: string, actions?: Array<{ type: string, link: string, text?: string }> }>} workflowSteps
 */
export function buildNavigationEdges(workflowSteps) {
  if (!workflowSteps?.length) return [];

  const navEntries = [];
  workflowSteps.forEach((step) => {
    (step.actions || []).forEach((action, actionIndex) => {
      if (action.type === 'navigation' && action.link) {
        navEntries.push({
          source: step.id,
          target: action.link,
          actionIndex,
          label: action.text || '',
        });
      }
    });
  });

  const groupCounts = new Map();
  navEntries.forEach(({ source, target }) => {
    const key = buildEdgePairKey(source, target);
    groupCounts.set(key, (groupCounts.get(key) || 0) + 1);
  });

  const groupIndices = new Map();

  return navEntries.map(({ source, target, actionIndex, label }) => {
    const key = buildEdgePairKey(source, target);
    const groupSize = groupCounts.get(key) || 1;
    const indexInGroup = groupIndices.get(key) || 0;
    groupIndices.set(key, indexInGroup + 1);

    return buildNavigationEdge({
      source,
      target,
      actionIndex,
      label,
      indexInGroup,
      groupSize,
    });
  });
}
